import { Box } from "../Box";
import { Button, type ButtonProps } from "../Button";
import { useTheme } from "../ThemeProvider";

import React, { type ReactNode, useMemo } from "react";
import { View, StyleSheet, Dimensions, ScrollView } from "react-native";

const { width, height } = Dimensions.get("window");

export interface FloatingActionBarProps {
  /**
   * Array of button configurations to display at the bottom of the floating action bar.
   * Supports up to 4 buttons.
   */
  buttons: ButtonProps[];

  /**
   * Optional content to display above the buttons in a scrollable area.
   * Can include any React elements like text, lists, or custom components.
   */
  actions?: ReactNode;

  /**
   * Whether to show a divider line between the actions content and buttons.
   * @default false
   */
  showDivider?: boolean;
}

export const FloatingActionBar = ({
  buttons,
  actions,
  showDivider = false,
}: FloatingActionBarProps) => {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        overlay: {
          position: "absolute",
          top: 0,
          left: 0,
          width,
          height,
          backgroundColor: theme.colors.background,
        },
        floatingContainer: {
          position: "absolute",
          bottom: 0,
          width: "100%",
          maxHeight: height * 0.9, // This is required to make sure that the 'actions' content doesn't take up the whole screen
          backgroundColor: theme.colors.background,
          overflow: "hidden",
        },
        contentContainer: {
          flexDirection: "column",
          paddingHorizontal: theme.spacing.s4,
          paddingTop: theme.spacing.s4,
          paddingBottom: theme.spacing.s4,
          flex: 1,
        },
        scrollView: {
          flexGrow: 0,
        },
        scrollContent: {
          paddingBottom: theme.spacing.s4,
        },
        actions: {
          width: "100%",
        },
        divider: {
          height: 1,
          backgroundColor: theme.colors.neutral200,
          marginLeft: -theme.spacing.s4,
          marginRight: -theme.spacing.s4,
          width: width,
        },
        buttonArea: {
          width: "100%",
          gap: theme.spacing.s3,
        },
        stackedButtonWrapper: {
          width: "100%",
          top: theme.spacing.s2,
          bottom: theme.spacing.s3,
          gap: theme.spacing.s3,
        },
      }),
    [theme],
  );

  // Only allow up to 4 buttons
  const displayButtons = buttons.slice(0, 4);

  // Calculate dynamic scroll height based on available space
  const scrollViewMaxHeight = useMemo(() => {
    const containerMaxHeight = height * 0.9;
    const buttonHeight = 48; // Approximate button height
    const buttonSpacing = theme.spacing.s3; // Gap between buttons
    const dividerHeight = showDivider ? 1 + theme.spacing.s2 + theme.spacing.s3 : 0; // divider + margins
    const containerPadding = theme.spacing.s4 * 2; // top + bottom padding

    const buttonsAreaHeight =
      displayButtons.length * buttonHeight + (displayButtons.length - 1) * buttonSpacing;

    const availableHeight =
      containerMaxHeight - buttonsAreaHeight - dividerHeight - containerPadding;

    // Ensure we use at least 60% of screen height, but allow more if available
    return Math.max(height * 0.6, availableHeight - 20); // 20px buffer
  }, [displayButtons.length, showDivider, theme.spacing]);

  return (
    <>
      <View style={styles.overlay} />
      <View style={[styles.floatingContainer]}>
        <View style={styles.contentContainer}>
          {actions && (
            <ScrollView
              style={[styles.scrollView, { maxHeight: scrollViewMaxHeight }]}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator
            >
              <Box sx={styles.actions}>{actions}</Box>
            </ScrollView>
          )}

          {showDivider && <View testID="floating-action-bar-divider" style={styles.divider} />}

          <View style={styles.buttonArea}>
            {displayButtons.map((buttonProps, index) => {
              return (
                <Box key={index} sx={styles.stackedButtonWrapper}>
                  <Button {...buttonProps} />
                </Box>
              );
            })}
          </View>
        </View>
      </View>
    </>
  );
};
